import { useEffect, useState, useMemo, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import EventIcon from '@mui/icons-material/Event';
import RotateRightIcon from '@mui/icons-material/RotateRight';

import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import {
  formatToIsoWithoutTimezone,
  getLastWeekDateRange,
  getLastMonthDateRange,
} from '~/utils/date';
import downloadFile from '~/utils/downloadFile';
import { getFileNameFromContentDisposition } from '~/utils/headers';
import { extractErrorMessage } from '~/utils/error';
import { getInitialValues, validate } from './form';
import Button from '~/ui/components/common/Button';
import SelectAll from '~/ui/components/inputs/SelectAll';
import DatePicker from '~/ui/components/inputs/DatePicker';

import { IBillingReportRequest } from '~/services/api/reports/types';
import { IFormValues } from './types';
import { DateRangeType } from '../types';
import styles from './BillingReport.module.scss';
import { IIdName } from '~/services/api/types';

const BillingReport = (): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [programs, setPrograms] = useState<IIdName[]>([]);
  const [isProgramsLoading, setIsProgramsLoading] = useState(false);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const initialValues = getInitialValues();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: validate,
    defaultValues: initialValues,
  });

  const programOptions = useMemo(
    () =>
      programs.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [programs.length],
  );

  const generateReport = async (payload: IBillingReportRequest) => {
    try {
      setIsProcessing(true);
      const { data, headers } = await api.reports.getBillingReport(payload);
      const fileName = getFileNameFromContentDisposition(headers['content-disposition']);
      downloadFile(window.URL.createObjectURL(new Blob([data])), fileName);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsProcessing(false);
    }
  };

  const onSubmitForm = (values: IFormValues) => {
    generateReport({
      ...values,
      startDate: formatToIsoWithoutTimezone(values.startDate),
      endDate: formatToIsoWithoutTimezone(values.endDate),
    });
  };

  const resetDateRange = (type: DateRangeType) => {
    let dateRange = getLastWeekDateRange();

    if (type === DateRangeType.Month) {
      dateRange = getLastMonthDateRange();
    }

    setValue('startDate', dateRange.startDate);
    setValue('endDate', dateRange.endDate);
  };

  const onMount = async () => {
    try {
      setIsProgramsLoading(true);
      const selectors = await api.reports.getSelectors().then(res => res.data);
      setPrograms(selectors.programs);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsProgramsLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className={styles.title}>Billing Report</div>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <SelectAll
            name="programIds"
            control={control}
            errors={errors}
            options={programOptions}
            isLoading={isProgramsLoading}
            label="Program Name"
            required
          />
        </Grid>
        <Grid item xs={6} />
        <Grid item xs={6}>
          <DatePicker
            placeholder=".. /.. /.."
            label="Start Date"
            name="startDate"
            maxDate={new Date()}
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            placeholder=".. /.. /.."
            label="End Date"
            name="endDate"
            maxDate={new Date()}
            control={control}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<EventIcon />}
            onClick={() => resetDateRange(DateRangeType.Week)}
          >
            Last Week
          </Button>
          <div className={styles.spacer} />
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<EventIcon />}
            onClick={() => resetDateRange(DateRangeType.Month)}
          >
            Last Month
          </Button>
        </Grid>
        <Grid item xs={6} className={styles.rightBtnCol}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<RotateRightIcon />}
            onClick={() => {
              reset(initialValues);
            }}
          >
            Reset
          </Button>
          <div className={styles.spacer} />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            isLoading={isProcessing}
            disabled={isProcessing}
          >
            Generate
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default BillingReport;
